import React, { Children, cloneElement, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import {
  ControlledMenu,
  FocusableItem,
  MenuDivider,
  MenuGroup,
  MenuItem,
  SubMenu,
  useMenuState
} from '@szhsin/react-menu';
import { sortBy } from 'lodash';
import { uniq } from 'lodash/array';
import PropTypes from 'prop-types';

import { ItemGroupTemplatesApi } from '../../../../../api';
import Input from '../../../../../common/data-entry/Input';
import Icon from '../../../../../common/general/Icon';
import { caseInsensitiveStringIncludes } from '../../../../../services/string';
import HighlightWithTooltip from '../../../../root/Container/Layout/MenuOptions/FindPatient/FindPatientPopup/FindPatientPopupItem/HighlightWithTooltip';
import { generateUrlByKey, useCurrentRoute } from '../../../../root/router';
import { NEW_TABLE } from '../../../patient-source/shared/Review/reviewConstants';

import './AddNewItemGroupMenu.scss';

export default function AddNewItemGroupMenu({ children, terminologyVersionGroupNumber, handleSave }) {
  const childrenRef = useRef(null);
  const { openMenu, closeMenu, toggleMenu, menuItemFocus, ...menuProps } = useMenuState();
  const [templates, setTemplates] = useState(null);
  const [searchString, setSearchString] = useState('');
  const [domainToShow, setDomainToShow] = useState([]);

  const rawUrl = !handleSave;
  const navigate = useNavigate();

  const { parent, params, key, name } = useCurrentRoute();
  const protocolIdentity = params?.protocolIdentity;
  const studyId = params?.studyId;

  const domainToTemplatesMap = useMemo(
    function() {
      if (!templates) {
        return {};
      }
      const filteredTemplates = searchString
        ? templates.filter(({ domainName, name }) => {
            return (
              caseInsensitiveStringIncludes(domainName, searchString) ||
              caseInsensitiveStringIncludes(name, searchString)
            );
          })
        : templates;
      return templatesMapper(filteredTemplates);
    },
    [templates, searchString]
  );

  const onTemplatesSubMenuChange = open => {
    if (!open || templates || !terminologyVersionGroupNumber) {
      return;
    }
    ItemGroupTemplatesApi.getItemGroupTemplatesByTerminologyVersionGroupNumber(terminologyVersionGroupNumber).then(
      ({ data }) => {
        setTemplates(data);
        setDomainToShow(data.map(({ domainName }) => domainName));
      },
      () => setTemplates([])
    );
  };

  const handleSaveAndNewItemGroupRedirect = () => {
    handleSave(false, null, true).then(res => {
      if (res !== 'SAVE_AND_NEW_SPECIFIC_REDIRECT') {
        return;
      }
      const redirectPath = resolveItemGroupRedirectPath();
      navigate(generateUrlByKey(redirectPath, params));
    });
  };

  const resolveItemGroupRedirectPath = () => {
    if (key.includes('Encounter Setup') && ['Item Group Edit', 'Table Edit'].includes(name)) {
      return `${parent.parent.key}.Item Groups List.Item Group Setup`;
    }
    if (['Item Group Table Template Setup', 'Item Group Template Setup'].includes(name)) {
      return `${parent.key}.Item Group Setup`;
    }
    if (['Item Group Setup', 'Table Setup'].includes(name)) {
      return key;
    }
    return parent.key;
  };

  const handleSaveAndNewTemplateRedirect = template => {
    handleSave(false, null, true).then(res => {
      if (res !== 'SAVE_AND_NEW_SPECIFIC_REDIRECT') {
        return;
      }
      const templateKey = template.type === NEW_TABLE ? 'Item Group Table Template Setup' : 'Item Group Template Setup';
      let templatesBaseParentPath = resolveTemplateBaseParentPath();
      navigate(
        generateUrlByKey(`${templatesBaseParentPath}.${templateKey}`, {
          itemGroupTemplateId: template.itemGroupTemplateId,
          protocolIdentity,
          studyId
        })
      );
    });
  };

  const resolveTemplateBaseParentPath = () => {
    if (key.includes('Encounter Setup') && ['Item Group Edit', 'Table Edit'].includes(name)) {
      return `${parent.parent.key}.Item Groups List`;
    }
    if (['Item Group Edit', 'Table Edit'].includes(name)) {
      return parent.parent.key;
    }
    if (
      ['Item Group Table Template Setup', 'Table Setup', 'Item Group Setup', 'Item Group Template Setup'].includes(name)
    ) {
      return parent.key;
    }
    return null;
  };

  const resolveTemplateUrl = template => {
    if (rawUrl) {
      return template.type === 'New Table'
        ? generateUrlByKey(`${key}.Item Group Table Template Setup`, {
            itemGroupTemplateId: template.itemGroupTemplateId,
            protocolIdentity,
            studyId
          })
        : generateUrlByKey(`${key}.Item Group Template Setup`, {
            itemGroupTemplateId: template.itemGroupTemplateId,
            protocolIdentity,
            studyId
          });
    }
    return null;
  };

  const renderSearchInput = () => (
    <Input
      onChange={({ target: { value } }) => {
        setSearchString(value);
      }}
      label="Search Template"
      validate={false}
      iconsAfter={<Icon>search</Icon>}
      value={searchString}
    />
  );

  return (
    <div className="add-new-item-group-menu">
      {Children.map(children, child =>
        cloneElement(child, {
          ref: childrenRef,
          onClick: openMenu
        })
      )}
      <ControlledMenu
        anchorRef={childrenRef}
        align="end"
        direction="bottom"
        onClose={onClose}
        {...menuProps}
        className="igl-add"
      >
        <MenuItem className="add-new-link">
          <Link
            onClick={event => {
              if (!rawUrl) {
                event.preventDefault();
                event.stopPropagation();
                event.nativeEvent.stopImmediatePropagation();
                handleSaveAndNewItemGroupRedirect();
              }
            }}
            to={
              rawUrl
                ? generateUrlByKey(`${key}.Item Group Setup`, {
                    studyId,
                    protocolIdentity
                  })
                : null
            }
          >
            New Item Group
          </Link>
        </MenuItem>
        <SubMenu
          onChange={({ open }) => onTemplatesSubMenuChange(open)}
          itemClassName="igt-sub"
          label={<span>New From Template</span>}
        >
          {!templates ? (
            <MenuItem>
              <CircularProgress />
            </MenuItem>
          ) : (
            <FocusableItem className="igl-search-template">{renderSearchInput}</FocusableItem>
          )}
          {templates && (
            <MenuGroup className="submenu-group">
              {Object.keys(domainToTemplatesMap).map((domainName, i) => {
                const domain = domainToTemplatesMap[domainName];
                return (
                  <MenuGroup key={i}>
                    <FocusableItem
                      className="igl-item-domain"
                      styles={{ hover: false }}
                      onClick={() =>
                        setDomainToShow(function(state) {
                          if (!state.includes(domainName)) {
                            return [...state, domainName];
                          }
                          return state.filter(e => e !== domainName);
                        })
                      }
                    >
                      {renderArrowDropDownIcon(domainName, domainToShow)}
                    </FocusableItem>
                    {domainToShow.includes(domainName) &&
                      domain.templates.map((e, i) => {
                        return (
                          <MenuItem className="igl-template" key={i}>
                            <Link
                              onClick={event => {
                                if (!rawUrl) {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  event.nativeEvent.stopImmediatePropagation();
                                  handleSaveAndNewTemplateRedirect(e);
                                }
                              }}
                              to={resolveTemplateUrl(e)}
                            >
                              <span className="value">
                                <HighlightWithTooltip word={e.name} referredLength={26} />
                              </span>
                            </Link>
                          </MenuItem>
                        );
                      })}
                    <MenuDivider />
                  </MenuGroup>
                );
              })}
            </MenuGroup>
          )}
        </SubMenu>
      </ControlledMenu>
    </div>
  );

  function onClose() {
    closeMenu();
  }
}

function templatesMapper(templates) {
  const obj = {};
  const domains = sortBy(templates.map(({ domainName }) => domainName));
  uniq(domains).forEach(function(e) {
    obj[e] = {
      templates: sortBy(
        templates.filter(({ domainName }) => domainName === e),
        'name'
      )
    };
  });
  return obj;
}

function renderArrowDropDownIcon(domainName, domainToShow) {
  return () => {
    return (
      <>
        <span>{domainName}</span>
        <Icon className="igl-icon">{domainToShow.includes(domainName) ? 'arrow_drop_down' : 'arrow_right'}</Icon>
      </>
    );
  };
}

AddNewItemGroupMenu.propTypes = {
  children: PropTypes.node,
  terminologyVersionGroupNumber: PropTypes.number
};
